import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

type Position = { x: number; y: number };

/** Wait for route data before resolving anchors or restoring a history entry. */
export default function RouteScroll() {
  const location = useLocation();
  const navigation = useNavigationType();
  const positions = useRef(new Map<string, Position>());

  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    const header = document.querySelector("header.navbar");
    const measure = () => {
      document.documentElement.style.setProperty(
        "--site-header-height", `${header?.getBoundingClientRect().height || 64}px`
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (header) observer.observe(header);
    return () => {
      observer.disconnect();
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    let finished = false;
    let frame = 0;
    const saved = navigation === "POP" ? positions.current.get(location.key) : undefined;
    const remember = () => {
      if (!finished) return;
      positions.current.set(location.key, { x: window.scrollX, y: window.scrollY });
      if (positions.current.size > 100) {
        positions.current.delete(positions.current.keys().next().value!);
      }
    };
    const restore = () => {
      if (finished || !document.querySelector("main [data-page-ready='true']")) return;
      let id = location.hash.slice(1);
      try { id = decodeURIComponent(id); } catch { /* Ignore malformed fragment escapes. */ }
      const target = id ? document.getElementById(id) : null;
      if (saved) {
        window.scrollTo({ left: saved.x, top: saved.y, behavior: "instant" as ScrollBehavior });
      } else if (target) {
        target.scrollIntoView({ block: "start", behavior: "instant" as ScrollBehavior });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
      }
      if (!saved && (navigation !== "POP" || target)) {
        const focus = target?.querySelector<HTMLElement>("h1, h2") || target ||
          document.querySelector<HTMLElement>("main h1, main [role='alert']");
        if (focus) {
          if (!focus.hasAttribute("tabindex")) {
            focus.setAttribute("tabindex", "-1");
            focus.addEventListener("blur", () => focus.removeAttribute("tabindex"), { once: true });
          }
          focus.focus({ preventScroll: true });
        }
      }
      finished = true;
      observer.disconnect();
      remember();
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(restore);
    };
    const observer = new MutationObserver(schedule);
    const main = document.querySelector("main");
    if (main) observer.observe(main, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-page-ready"] });
    schedule();
    window.addEventListener("scroll", remember, { passive: true });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", remember);
    };
  }, [location.key, location.pathname, location.search, location.hash, navigation]);

  return null;
}
